import {
  HttpClient,
  HttpErrorResponse,
  HttpResponse,
  HttpStatusCode,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { catchError, map, of, OperatorFunction, tap, throwError } from 'rxjs';
import { AvailableSchedulePeriod } from '@techspert-io/schedule-calendar';
import { InterviewPeriod } from '../models/interview-period.models';

/**
 * Statuses for a save that conflicts with a change made elsewhere: either the
 * interview period changed since it was loaded, or it didn't exist when loaded
 * and has since been saved
 */
export const INTERVIEW_PERIOD_CONFLICT_STATUSES: readonly HttpStatusCode[] = [
  HttpStatusCode.PreconditionFailed,
  HttpStatusCode.PreconditionRequired,
];

@Injectable({
  providedIn: 'root',
})
export class InterviewPeriodService {
  private readonly baseUrl = '/client-portal-opportunities/opportunities';

  constructor(
    private http: HttpClient,
    private toastService: ToastrService
  ) {}

  getByOpportunityId(opportunityId: string) {
    return this.http
      .get<InterviewPeriod>(
        `${this.baseUrl}/${opportunityId}/interview-period`,
        { observe: 'response' }
      )
      .pipe(
        map((response) => this.mapResponseWithLastModified(response)),
        this.handleError('Failed to retrieve interview period')
      );
  }

  updateAvailable(
    opportunityId: string,
    periods: AvailableSchedulePeriod[],
    lastModified?: string
  ) {
    return this.http
      .post<AvailableSchedulePeriod[]>(
        `${this.baseUrl}/${opportunityId}/interview-period/available`,
        periods,
        {
          ...(lastModified && {
            headers: {
              'If-Unmodified-Since': lastModified,
            },
          }),
          observe: 'response',
        }
      )
      .pipe(
        tap(() => {
          this.toastService.success('Interview period updated');
        }),
        map((response) => this.mapResponseWithLastModified(response)),
        this.handleError(
          'Failed to update interview period',
          INTERVIEW_PERIOD_CONFLICT_STATUSES
        )
      );
  }

  private handleError<T>(
    context: string,
    throwOn: readonly HttpStatusCode[] = []
  ): OperatorFunction<T, T | null> {
    return catchError((error: unknown) => {
      const isHttpError = error instanceof HttpErrorResponse;

      if (isHttpError && throwOn.includes(error.status)) {
        return throwError(() => error);
      }

      const errorMessage = isHttpError && error.error?.message;

      this.toastService.error(
        [context, errorMessage || 'unknown error'].join(' - ')
      );

      return of(null);
    });
  }

  private mapResponseWithLastModified<T>(response: HttpResponse<T>) {
    return {
      body: response.body,
      lastModified: response.headers.get('last-modified'),
    };
  }
}
